import React, { useEffect, useState } from 'react';
import { UserAccount } from '../../types/user';
import { useAppSelector } from '../../hooks/redux';
import { Edit2, Trash2, CheckCircle, XCircle, Clock, Shield, Lock, UserCheck, UserX, Eye } from 'lucide-react';

interface UserTableProps {
  users: UserAccount[];
  onEdit: (user: UserAccount) => void;
  onDelete: (userId: number) => void;
  onToggleStatus: (userId: number) => void;
  onChangePassword: (user: UserAccount) => void;
  onViewDetails: (user: UserAccount) => void;
  loading?: boolean;
}

const UserTable: React.FC<UserTableProps> = ({ 
  users, 
  onEdit, 
  onDelete, 
  onToggleStatus,
  onChangePassword,
  onViewDetails,
  loading = false 
}) => {
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Update current time every minute to refresh relative timestamps
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);
  
  const getRoleBadge = (role: string) => {
    const roleConfig = {
      manager: { label: 'Manager', color: 'bg-purple-100 text-purple-800' },
      executive1: { label: 'Executive 1', color: 'bg-blue-100 text-blue-800' },
      executive2: { label: 'Executive 2', color: 'bg-green-100 text-green-800' },
      worker: { label: 'Worker', color: 'bg-gray-100 text-gray-800' }
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.worker;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };  const formatLastLogin = (lastLogin: string | null) => {
    if (!lastLogin) {
      return (
        <div className="flex items-center text-gray-400">
          <Clock className="h-4 w-4 mr-1" />
          <span className="text-sm">Never</span>
        </div>
      );
    }
    
    try {
      const loginDate = new Date(lastLogin);
      const diffInMinutes = Math.floor((currentTime.getTime() - loginDate.getTime()) / (1000 * 60));
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);
      
      // Less than 2 minutes - "Just now"
      if (diffInMinutes < 2) {
        return (
          <div className="flex items-center text-green-600">
            <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
            <span className="text-sm">Just now</span>
          </div>
        );
      }
      // Less than 60 minutes - show minutes
      else if (diffInMinutes < 60) {
        return (
          <div className="flex items-center text-green-600">
            <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
            <span className="text-sm">{diffInMinutes}m ago</span>
          </div>
        );
      }
      // Less than 24 hours - show hours
      else if (diffInHours < 24) {
        return (
          <div className="flex items-center text-blue-600">
            <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
            <span className="text-sm">{diffInHours}h ago</span>
          </div>
        );
      }
      // More than 24 hours - show date
      else {
        return (
          <div className="flex items-center text-gray-600">
            <Clock className="h-4 w-4 mr-1" />
            <div>
              <div className="text-sm">{formatDate(lastLogin)}</div>
              <div className="text-xs text-gray-400">
                {diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`}
              </div>
            </div>
          </div>
        );
      }
    } catch (error) {
      return (
        <div className="flex items-center text-red-400">
          <Clock className="h-4 w-4 mr-1" />
          <span className="text-sm">Invalid date</span>
        </div>
      );
    }
  };

  const canDeleteUser = (user: UserAccount) => {
    // Don't allow deleting the current user
    if (currentUser && user.username === currentUser.username) {
      return false;
    }
    
    // Don't allow deleting the main admin user in demo mode
    if (user.id === 1 && user.username === 'admin') {
      return false;
    }
    
    return true;
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center relative">
                        <span className="text-sm font-medium text-gray-700">
                          {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || user.username.charAt(0).toUpperCase()}
                        </span>
                        {currentUser && user.username === currentUser.username && (
                          <div className="absolute -top-1 -right-1 h-4 w-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <Shield className="h-2 w-2 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.full_name}
                        {currentUser && user.username === currentUser.username && (
                          <span className="ml-2 text-xs text-blue-600">(You)</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                      <div className="text-xs text-gray-400">@{user.username}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getRoleBadge(user.role)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {user.is_active ? (                      <>
                        <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm text-green-700">Active</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-red-500 mr-1" />
                        <span className="text-sm text-red-700">Inactive</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(user.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatLastLogin(user.last_login_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-1">
                    {/* View Details */}
                    <button
                      onClick={() => onViewDetails(user)}
                      className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-50"
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>

                    {/* Edit User */}
                    <button
                      onClick={() => onEdit(user)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                      title="Edit user"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>

                    {/* Toggle Status */}
                    <button
                      onClick={() => onToggleStatus(user.id)}
                      className={`p-1 rounded ${
                        user.is_active 
                          ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                          : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                      }`}
                      title={user.is_active ? "Deactivate user" : "Activate user"}
                    >
                      {user.is_active ? (
                        <UserX className="h-4 w-4" />
                      ) : (
                        <UserCheck className="h-4 w-4" />
                      )}
                    </button>

                    {/* Change Password */}
                    <button
                      onClick={() => onChangePassword(user)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                      title="Change password"
                    >
                      <Lock className="h-4 w-4" />
                    </button>

                    {/* Delete User */}
                    {canDeleteUser(user) ? (
                      <button
                        onClick={() => onDelete(user.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        disabled
                        className="text-gray-400 p-1 rounded cursor-not-allowed"
                        title={currentUser && user.username === currentUser.username 
                          ? "Cannot delete your own account" 
                          : "Cannot delete this user"
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {users.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">No users found</div>
        </div>
      )}
    </div>
  );
};

export default UserTable;
